import { faTasks } from '@fortawesome/free-solid-svg-icons';
import './TimesheetManagementIndex.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LinearProgress } from '@mui/material';
import React from 'react';
import { withRouter } from 'react-router-dom';

import * as moment from 'moment';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import CommonContext from '../Common';
import { ApiRoutes } from '../ApiRoutes';
import {
    getFilterModel,
    createDataSource,
    createGridOptions,
    DataGrid,
    indexCellRenderer,
    LinkCellRenderer,
    IconCellRenderer,
    VariableLinkCellRenderer,
    TextFilterDefaults,
    DateFilterDefaults,
    getServerSideRowRequest,
    TimesheetNumberCellRenderer,
    EmployeeAccountStatusRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import {
    PageHeading,
    PageWrap,
    onFieldChange,
    toasty,
    SmallButton,
    FlexCenterRow,
} from '../common/forms/FormElements';
import TimesheetDetails from '../timesheet/TimesheetDetails';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { util } from '../Util';
import authService from '../api-authorization/AuthorizeService';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { AppNavPaths } from '../AppNavPaths';
import { MomentDateFormat } from '../MomentDateFormat';
import { getTenantUserProfile } from '../common/TenantUserProfile';
import cls from 'classnames';
import { TimesheetStatus } from '../timesheet/Timesheet';
import { TimesheetTypes } from './TimesheetManagement';
import TimesheetExportButton from './TimesheetExportButton';

const filterStorageKey = 'timesheetManagement_gridFilterSettings';

class TimesheetManagementIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.timesheetDetailsRef = React.createRef();

        this.state = {
            loading: true,
            rowData: [],
            rowsSelected: [],
            timesheetDetailsOpen: false,
            selectedDetails: [],
            currentTimesheetId: null,
            isExcelExporting: false,
            isExporting: false,
            loadingReport: false,
            showPrintModal: false,
            showExcelExportModal: false,
            onlyMyTimesheets: false,
            isAdmin: false,
            isExporter: false,
        };

        // this.onRowClicked = this.onRowClicked.bind(this);
        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
        this.setState = (state, callback) => {};
    }

    onChange = onFieldChange;

    onNavBack = async () => {
        const { currentTimesheetId } = { ...this.state };
        const rows = [];
        this.state.gridOptions.api.forEachNode((node) => rows.push(node.data));
        const navArray = rows.map((x) => x.id);

        if ((navArray ?? []).length > 0) {
            let currentIndex = navArray.indexOf(currentTimesheetId);
            const previousIndex = --currentIndex % navArray.length;
            const id = navArray[previousIndex];

            if (id) {
                this.setState({ currentTimesheetId: id });
                await this.context.setFormOpened(true);
                this.timesheetDetailsRef.current.open(id);
            }
        }
    };

    onNavForward = async () => {
        const { currentTimesheetId } = { ...this.state };
        const rows = [];
        this.state.gridOptions.api.forEachNode((node) => rows.push(node.data));
        const navArray = rows.map((x) => x.id);

        if ((navArray ?? []).length > 0) {
            let currentIndex = navArray.indexOf(currentTimesheetId);
            const nextIndex = ++currentIndex % navArray.length;
            const id = navArray[nextIndex];

            if (id) {
                this.setState({ currentTimesheetId: id });
                await this.context.setFormOpened(true);
                this.timesheetDetailsRef.current.open(id);
            }
        }
    };

    onExportClick = async () => {
        this.setState({ isExporting: true });

        const { gridOptions } = this.state;

        // https://www.ag-grid.com/javascript-data-grid/grid-api/#reference-filter
        const agFilterModel = gridOptions.api.getFilterModel();
        const filterModel = getFilterModel(agFilterModel);

        try {
            const response = await util.fetch.post(
                ApiRoutes.contractTimesheetManagement.export(),
                {
                    filterModel,
                }
            );

            if (response && response.data) {
                const exportId = response.data;
                toasty.success(
                    'Timesheet Export successful',
                    'Please wait while the export file is generated and downloaded.'
                );

                await this.downloadExport(exportId);

                this.setState({ isExporting: false });

                gridOptions.refresh();
            } else {
                toasty.error(response);
            }
        } catch {
            toasty.error('Error exporting timesheets');
        }

        this.setState({ isExporting: false });
    };

    onTimesheetDetailsClosed = async () => {
        await this.context.setFormOpened(false);
        await this.setState({ selectedRow: null });
        this.state.gridOptions.refresh();
    };

    getExportDateMessage(agFilterModel) {
        const maxDaysToExport = 14;

        const from = moment(agFilterModel.Date.dateFrom);
        const to = moment(agFilterModel.Date.dateTo);
        const dateDiff = Math.abs(to.diff(from, 'days'));
        const isDateOK = dateDiff <= maxDaysToExport;

        const ret = isDateOK
            ? ''
            : `Exports must be for no more than ${maxDaysToExport} days.`;

        return ret;
    }

    getExportDispatchLocationMessage(agFilterModel) {
        const isLocOK = !!agFilterModel.DispatchLocation?.value;

        const ret = isLocOK
            ? ''
            : 'Exports must be for a single Dispatching location only.';

        return ret;
    }

    // 2022-09-09 - M. Nicol
    // Export is allowed if the date range is <= 7 days
    // and at least has the possibility of containing Approved timesheets.
    getExportMessageAndAllow() {
        const { gridOptions } = this.state;

        const agFilterModel = gridOptions.api.getFilterModel();

        // Date range must be no more than 7 days.
        // Timesheet status must be null (all) or 7 (Approved).
        // Must be for 1 dispatch location only (Removed for now - re-eanble and include in "msg" if desired.)
        // Empty message means all passed.

        const dateMessage = this.getExportDateMessage(agFilterModel);
        const statusMessage = this.getExportStatusMessage(agFilterModel);
        // const dispatchLocationMessage = this.getExportDispatchLocationMessage(agFilterModel);

        let message = `${dateMessage} ${statusMessage}`.trim();

        const isAllowed = message === '';

        if (isAllowed) {
            message =
                'Export will be generated based upon search criteria entered on the timesheet screen.';
        }

        return {
            message,
            isAllowed,
        };
    }

    getExportStatusMessage(agFilterModel) {
        const isStatusOK = (agFilterModel.Status?.value ?? 7) == 7;

        const ret = isStatusOK
            ? ''
            : 'Exports must be for Approved timesheets only.';

        return ret;
    }

    downloadExport = async (id) => {
        if (!id) {
            return;
        }

        this.setState({ loadingReport: true });

        await util.fetch
            .downloadFile(
                ApiRoutes.timesheetManagement.download(id),
                null,
                `Timesheet-${id}.zip`
            )
            .catch(this.handleSaveError);

        this.setState({ loadingReport: false });
    };

    exportToExcel = async () => {
        await this.setState({
            isExcelExporting: true,
        });
        const { gridOptions } = { ...this.state };
        const req = getServerSideRowRequest(gridOptions);
        const fileName = `TimesheetManagementSearch_${moment().format(
            'YYYYMMDD_hhmmss'
        )}.xlsx`;
        try {
            await util.fetch.downloadFile(
                ApiRoutes.timesheetManagement.excelExport(),
                req,
                fileName
            );
        } catch (err) {
            toasty.error(`Error exporting data: ${err}`);
        } finally {
            await this.setState({ isExcelExporting: false });
        }
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    openIndicatedTimesheetDetails = (row) => {
        if (!row || !row.data) {
            return null;
        }

        const { data } = row;

        const {
            timesheetId,
            lowboyTimesheetId,
            timeOffRequestId,
            dailyTimesheetId,
        } = data;

        if (timesheetId) {
            this.props.history.push(
                `${AppNavPaths.CrewTimesheet}/${timesheetId}`
            );
        } else if (lowboyTimesheetId) {
            this.props.history.push(
                `${AppNavPaths.LowboyTimesheet}/${lowboyTimesheetId}`
            );
        } else if (timeOffRequestId) {
            this.props.history.push(
                `${AppNavPaths.TimeOffRequest}/${timeOffRequestId}`
            );
        } else if (dailyTimesheetId) {
            this.props.history.push(
                `${AppNavPaths.DailyTimesheet}/id/${dailyTimesheetId}`
            );
        } else {
            console.error('Unable to process data.');
            console.error({ data });

            toasty.error('Unable to route to proper destination.');
        }
    };

    timesheetTypeRenderer = (e) => {
        if (!e || !e.data) {
            return null;
        }

        const { data } = e;

        const {
            timesheetId,
            lowboyTimesheetId,
            timeOffRequestId,
            dailyTimesheetId,
        } = data;

        if (timesheetId) {
            return 'Crew';
        }

        if (lowboyTimesheetId) {
            return 'Lowboy';
        }

        if (timeOffRequestId) {
            return 'Time Off Request';
        }

        if (dailyTimesheetId) {
            return 'Timesheet';
        }

        return 'Unknown';
    };

    getPriorSunday = (momentDate) => {
        if (momentDate.weekday() == 0) {
            return momentDate;
        }

        return this.getPriorSunday(momentDate.subtract(1, 'day'));
    };

    getSavedFilterModel = () => {
        try {
            const json = sessionStorage.getItem(filterStorageKey);
            return json ? JSON.parse(json) : null;
        } catch {
            return null;
        }
    };

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();

        if (isAuthenticated) {
            const [timesheetStatuses, userProfile] = await Promise.all([
                util.fetch.js(ApiRoutes.typeAheads.timesheetStatuses()),

                getTenantUserProfile(),
            ]);

            const gridOptions = createGridOptions(this);
            const context = this;

            gridOptions.components = {
                selectFilter: DataGridSelectFilter,
                selectFloatingFilter: DataGridSelectFloatingFilter,
                nameRenderer: LinkCellRenderer,
                iconRenderer: IconCellRenderer,
                timesheetNumberRenderer: TimesheetNumberCellRenderer,
                variableLinkRenderer: VariableLinkCellRenderer,
                employeeAccountStatusRenderer: EmployeeAccountStatusRenderer,
                timesheetTypeRenderer: this.timesheetTypeRenderer,
            };

            const { userPermissions } = userProfile;

            const isAdmin =
                userPermissions &&
                userPermissions.includes('timesheet.approve');

            const isElevated =
                userPermissions && userPermissions.includes('timesheet.accept');

            const isExporter =
                userPermissions && userPermissions.includes('timesheet.export');

            // this allows row colors to be set
            gridOptions.rowClassRules = {
                'ag-row-warning': (params) =>
                    (params.data ?? {}).isClientCancelled === true,
                'ag-row-danger': (params) =>
                    (params.data ?? {}).isEmergency === true ||
                    ((params.data ?? {}).isZeroHours === true &&
                        params?.data?.status !== 'New'),
            };

            let initialStatusFilterValue = null;

            if (isAdmin) {
                initialStatusFilterValue = TimesheetStatus.Validated;
            } else if (isElevated) {
                initialStatusFilterValue = TimesheetStatus.Submitted;
            }

            const savedFilters = this.getSavedFilterModel();
            if (savedFilters) {
                initialStatusFilterValue = savedFilters.Status?.value ?? null;
            }

            const timesheetStatusFilterParams = {
                suppressFilterButton: true,
                options: timesheetStatuses,
                optionsLabel: 'label',
                optionsValue: 'value',
                initialFilterValue: initialStatusFilterValue,
            };

            const timesheetTypeFilterParams = {
                suppressFilterButton: true,
                options: TimesheetTypes,
                optionsLabel: 'label',
                optionsValue: 'value',
                initialFilterValue:
                    savedFilters?.timesheetManagementTypeId?.value ?? null,
            };

            const statusValueGetter = (row) => {
                const timesheetStatusId = row?.data?.timesheetStatusId;

                const timesheetStatus = timesheetStatuses.find(
                    (ts) => ts.value == timesheetStatusId
                );

                if (!timesheetStatus) {
                    return null;
                }

                if (
                    timesheetStatus.value == TimesheetStatus.Exported &&
                    row?.data?.timesheetExportId
                ) {
                    return `Download Export #${row.data.timesheetExportId}`;
                }

                return timesheetStatus.label;
            };

            gridOptions.columnDefs = [
                {
                    headerName: '',
                    valueGetter: 'node.id',
                    sortable: false,
                    hide: true,
                    flex: 1,
                    maxWidth: 35,
                    minWidth: 35,
                    cellRenderer: indexCellRenderer,
                },
                {
                    colId: 'Date',
                    headerName: 'Date',
                    field: 'date',
                    sort: { direction: 'desc', priority: 0 },
                    sortable: false,
                    filter: 'agDateColumnFilter',
                    filterParams: DateFilterDefaults,
                    flex: 2.2,
                    cellRenderer: (d) => {
                        if (!d || !d.data) {
                            return null;
                        }

                        const date = d.data.date;

                        return moment(date).format(
                            MomentDateFormat.WeekDayNameAndDate
                        );
                    },
                },
                {
                    colId: 'contractNumber',
                    headerName: 'Job #',
                    field: 'contractNumber',
                    flex: 1.1,
                    filter: 'agTextColumnFilter',
                    sortable: false,
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    maxWidth: 125,
                    minWidth: 125,
                },
                {
                    colId: 'contractDescription',
                    headerName: 'Job Description',
                    field: 'contractDescription',
                    flex: 4,
                    filter: 'agTextColumnFilter',
                    sortable: false,
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                },
                {
                    colId: 'CrewLead',
                    headerName: 'Crew Lead',
                    field: 'crewLead',
                    flex: 2,
                    filter: 'agTextColumnFilter',
                    sortable: false,
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'employeeAccountStatusRenderer',
                    cellRendererParams: {
                        nameField: 'crewLead',
                    },
                    maxWidth: 200,
                    minWidth: 200,
                },
                {
                    colId: 'TimesheetNumber', // leave this, it doesnt map to an ef prop but is triggered for the sort on the dynamic field.
                    headerName: 'Timesheet #',
                    field: 'timesheetNumber',
                    flex: 1.5,
                    filter: 'agTextColumnFilter',
                    sortable: false,
                    filterParams: TextFilterDefaults,
                    floatingFilterComponentParams: {
                        suppressFilterButton: true,
                    },
                    cellRenderer: 'timesheetNumberRenderer',
                },
                {
                    colId: 'timesheetManagementTypeId',
                    headerName: 'Type',
                    field: 'timesheetManagementTypeId',
                    sortable: false,
                    flex: 2,
                    tooltipField: 'type',
                    cellRenderer: 'timesheetTypeRenderer',
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: timesheetTypeFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: timesheetTypeFilterParams,
                    maxWidth: 190,
                    minWidth: 190,
                },
                {
                    colId: 'timesheetStatusId',
                    headerName: 'Status',
                    field: 'timesheetStatusId',
                    sortable: false,
                    flex: 2,
                    tooltipField: 'status',
                    cellRenderer: 'variableLinkRenderer',
                    cellRendererParams: (r) => ({
                        clicked: this.downloadExport,
                        isLink: !!r.data?.timesheetExportId && isExporter,
                        title: 'Download Export',
                        idField: 'timesheetExportId',
                        valueGetter: statusValueGetter,
                    }),
                    filter: 'selectFilter',
                    floatingFilter: true,
                    filterParams: timesheetStatusFilterParams,
                    floatingFilterComponent: 'selectFloatingFilter',
                    floatingFilterComponentParams: timesheetStatusFilterParams,
                    maxWidth: 190,
                    minWidth: 190,
                },
                {
                    minWidth: 65,
                    headerName: 'View',
                    sortable: false,
                    cellRenderer: 'iconRenderer',
                    cellRendererParams: (r) => {
                        return {
                            clicked: this.openIndicatedTimesheetDetails.bind(
                                this,
                                r
                            ),
                            iconClass: 'fa-eye',
                            noRightMargin: true,
                            fullWidth: true,
                        };
                    },
                },
                {
                    colId: 'id',
                    minWidth: 75,
                    maxWidth: 75,
                    headerName: 'Print',
                    sortable: false,
                    cellRenderer: 'iconRenderer',
                    cellRendererParams: {
                        clicked: this.printTimesheet,
                        idField: 'id',
                        iconClass: 'fa-print',
                        noRightMargin: true,
                        fullWidth: true,
                    },
                },
            ];

            // API returns an object with counts as well as rowData for the grid.
            // Save counts to the state, and return the rowData to the grid.
            gridOptions.postProcessData = (data) => {
                context.setState({
                    rowData: data,
                    scoreboardData: { ...data.counts } ?? null,
                });

                return { ...data.result };
            };

            // On the grid's first load, we want to apply the fsilters immediately from the state population,
            // before the API call is made.
            let filtersRestored = false;
            gridOptions.onFilterChanged = () => {
                if (!filtersRestored) {
                    return;
                }
                const filterModel = gridOptions.api.getFilterModel();
                sessionStorage.setItem(
                    filterStorageKey,
                    JSON.stringify(filterModel)
                );
            };

            gridOptions.onGridReady = (params) => {
                if (savedFilters) {
                    params.api.setFilterModel(savedFilters);
                } else {
                    const start = this.getPriorSunday(
                        moment(moment().subtract(8, 'days'))
                    ).format('YYYY-MM-DD');

                    const end = moment(moment().subtract(1, 'days')).format(
                        'YYYY-MM-DD'
                    );

                    params.api.setFilterModel({
                        Date: {
                            filterType: 'date',
                            type: 'inRange',
                            dateFrom: start,
                            dateTo: end,
                        },
                    });
                }
                params.api.onFilterChanged();

                // Filters are created and applied asynchronously; only start persisting once settled.
                window.setTimeout(() => {
                    filtersRestored = true;
                }, 1000);

                gridOptions.customParametersFunction = () => ({
                    onlyMyTimesheets: this.state.onlyMyTimesheets,
                });

                params.api.setDatasource(context.state.dataSource);
                context.setState({ gridApi: params.api });
            };

            const dataSource = createDataSource(
                ApiRoutes.contractTimesheetManagement.search(),
                gridOptions
            );

            this.setState({
                loading: false,
                gridOptions,
                dataSource,
                isRejectingTimesheet: false,
                isAdmin,
                isExporter,
            });
        }
    };

    printTimesheet = (id) => {
        // if (id) {
        //     window.open(ApiRoutes.report.timesheet(id), '_self');
        // }

        console.debug({ id });
        this.togglePrintModal();
    };

    toggleRejectTimesheetModal = () => {
        const { showRejectTimesheetModal } = this.state;
        this.setState({ showRejectTimesheetModal: !showRejectTimesheetModal });
    };

    togglePrintModal = () => {
        const { showPrintModal } = this.state;

        this.setState({ showPrintModal: !showPrintModal });
    };

    toggleExportExcelModal = () => {
        const { showExcelExportModal } = this.state;

        this.setState({ showExcelExportModal: !showExcelExportModal });
    };

    toggleOnlyMyTimesheets = () => {
        const { onlyMyTimesheets } = this.state;

        this.setState({ onlyMyTimesheets: !onlyMyTimesheets }, () => {
            this.state.gridOptions.refresh();
        });
    };

    render() {
        const {
            loading,
            rowData,
            gridOptions,
            scoreboardData,
            loadingReport,
            showPrintModal,
            showExcelExportModal,
            saving,
            onlyMyTimesheets,
            isAdmin,
        } = this.state;

        if (loading) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        let counts = {
            notSubmitted: 0,
            rejected: 0,
            submitted: 0,
            payrollRejected: 0,
        };

        if (scoreboardData) {
            counts = scoreboardData;
        }

        const countNotSubmittedClass = `mr-3 badge badge badge-lg ${
            counts.notSubmitted === 0
                ? 'badge-success p-2'
                : 'badge-warning p-2'
        }`;

        const countRejectedClass = `mr-3 badge badge-lg ${
            counts.rejected === 0 ? 'badge-success p-2' : 'badge-danger p-2'
        }`;

        const countSubmittedClass = `mr-3 badge badge-lg ${
            counts.submitted === 0 ? 'badge-success p-2' : 'badge-warning p-2'
        }`;

        const countPayrollRejectedClass = `mr-3 badge badge-lg ${
            counts.payrollRejected === 0
                ? 'badge-success p-2'
                : 'badge-danger p-2'
        }`;

        const { tenant } = this.context;
        const timesheetExportEnabled =
            tenant && !!tenant.tenantSettings.timesheetExportEnabled;

        const billingEnabled =
            tenant &&
            tenant.tenantSettings &&
            tenant.tenantSettings.billingEnabled;

        return (
            <>
                <PageWrap>
                    <PageHeading>
                        <FontAwesomeIcon
                            icon={faTasks}
                            className="mr-2 text-muted"
                        />
                        <span>Timesheet Management</span>

                        <TimesheetExportButton
                            {...this.state}
                            timesheetExportEnabled={timesheetExportEnabled}
                            onExportClick={this.onExportClick}
                        />
                    </PageHeading>

                    <FlexCenterRow
                        className={cls('pt2', {
                            'pb-3': isAdmin,
                        })}
                        id="timesheetWorkflowStatus"
                    >
                        <span
                            style={{ minWidth: '100px' }}
                            className="mr-2 text-muted font-weight-bold p-2"
                        >
                            Field Actions:
                        </span>
                        <span
                            style={{ minWidth: '100px' }}
                            className={countNotSubmittedClass}
                        >
                            Not Submitted
                            <span className="ml-2">{counts.notSubmitted}</span>
                        </span>
                        <span
                            style={{ minWidth: '100px' }}
                            className={countRejectedClass}
                        >
                            Rejected
                            <span className="ml-2">{counts.rejected}</span>
                        </span>

                        <span
                            style={{ minWidth: '100px' }}
                            className="mr-2 text-muted font-weight-bold p-2"
                        >
                            Office Actions:
                        </span>
                        <span
                            style={{ minWidth: '100px' }}
                            className={countSubmittedClass}
                        >
                            Submitted
                            <span className="ml-2">{counts.submitted}</span>
                        </span>

                        {billingEnabled && (
                            <span
                                style={{ minWidth: '100px' }}
                                className={countPayrollRejectedClass}
                            >
                                Billing Payroll Rejected
                                <span className="ml-2">
                                    {counts.payrollRejected}
                                </span>
                            </span>
                        )}
                    </FlexCenterRow>

                    {!isAdmin && (
                        <FlexCenterRow>
                            <label
                                className="ml-4 mb-0 d-flex align-items-center text-muted"
                                style={{
                                    cursor: 'pointer',
                                    fontWeight: 'normal',
                                }}
                            >
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    checked={onlyMyTimesheets}
                                    onChange={this.toggleOnlyMyTimesheets}
                                />
                                Only my timesheets
                            </label>
                        </FlexCenterRow>
                    )}

                    <DataGridToolbar
                        entity="Timesheet Management"
                        gridApi={this.state.gridApi}
                        dataSource={this.state.dataSource}
                        hideAdd
                        hideExcelButton
                        hideClearFilterButton
                        gridOptions={this.state.gridOptions}
                    >
                        <SmallButton
                            disabled={saving}
                            onClick={this.onExportToExcelClicked}
                        >
                            <i className="fa fa-file-excel fa-lg" />
                            <span className="ml-2 small-viewport-hide">
                                Export to Excel
                            </span>
                        </SmallButton>
                    </DataGridToolbar>

                    {!!loadingReport && <CustomCircularProgress />}

                    {!loadingReport && (
                        <DataGrid
                            domLayout="normal"
                            rowData={rowData}
                            gridOptions={gridOptions}
                            gridStatus={this.state.gridStatus}
                        />
                    )}
                </PageWrap>
                <TimesheetDetails
                    ref={this.timesheetDetailsRef}
                    show={this.state.timesheetDetailsOpen}
                    toggleShow={(open) =>
                        this.setState({ timesheetDetailsOpen: open })
                    }
                    onClose={this.onTimesheetDetailsClosed}
                    onAccept={this.onAcceptTimesheet}
                    onReject={this.onRejectTimesheet}
                    isTimesheetForm={false}
                    isTimesheet
                    isPayroll={false}
                    isBilling={false}
                    showNav
                    onNavBack={this.onNavBack}
                    onNavForward={this.onNavForward}
                />

                <Modal isOpen={showPrintModal} toggle={this.togglePrintModal}>
                    <ModalHeader toggle={this.togglePrintModal}>
                        Print Not Yet Supported
                    </ModalHeader>
                    <ModalBody>
                        Support for printing timesheets is not yet implemented,
                        pending documentation and discussion.
                    </ModalBody>
                </Modal>

                <Modal
                    isOpen={showExcelExportModal}
                    toggle={this.toggleExportExcelModal}
                >
                    <ModalHeader toggle={this.toggleExportExcelModal}>
                        Export Not Yet Supported
                    </ModalHeader>
                    <ModalBody>
                        Support for exporting timesheets is not yet implemented,
                        pending documentation and discussion.
                    </ModalBody>
                </Modal>
            </>
        );
    }
}

export default withRouter(TimesheetManagementIndex);
